import ks from "./kBrowserShared";
import { KNum3 } from "./kNum3";

/**
 * kMotion - the ONE place that decides whether shared components animate.
 *
 * PRODUCT DECISION (2026-07-31, Peter): the components ALWAYS animate by
 * default, whatever the OS accessibility setting says. A shared component that
 * silently went still on a machine with "reduce motion" turned on looked broken
 * rather than considerate - text simply switched over with no swap at all. Note
 * that Windows Server reports reduced-motion BY DEFAULT, so on those machines
 * the entire library was permanently static; kEditableTable had already been
 * given the same treatment for the same reason (see its AddStyles comment).
 *
 * So `RespectSystemSetting` defaults to FALSE and every check routes through
 * here. The capability is kept, not deleted: a project that WANTS to honour the
 * OS setting turns it on once at startup and everything follows -
 *
 *     kMotion.RespectSystemSetting = true;
 *     kMotion.Sync();
 *
 * `Sync()` stamps `data-kreducedmotion="1"` on <html>, which is what the CSS
 * carve-outs in every component key off. Those used to be
 * `@media (prefers-reduced-motion: reduce)` blocks, which the browser applied
 * whether the product wanted it or not; as attribute selectors they are inert
 * until a project opts in. Nothing needs to call Sync() for the DEFAULT.
 *
 * WHY THIS IS ITS OWN FILE, not part of kAnimateTexts: `kalmstromShared4`
 * imports `kModalDialog`, so anything kModalDialog imports is inside an import
 * CYCLE with ks. kAnimateTexts declares `class kAnimTextObj extends kObject`,
 * which needs `kObject` at module-evaluation time - importing it from
 * kModalDialog threw "Cannot access 'kObject' before initialization" and took
 * the whole page down. This module has no `extends` and touches `ks` only inside
 * method bodies, so it is safe to import from anywhere in the cycle.
 */
export class kMotion {

    /** FALSE by design - see the class comment. Set to true, then call Sync(),
     *  to hand control back to the operating system. */
    public static RespectSystemSetting: boolean = false;

    /** True only when a project has opted in AND the OS asks for reduced motion. */
    public static IsReduced = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (kMotion.RespectSystemSetting !== true) { return false; }
            return kMotion.OsProbeSafe("kMotion.IsReduced");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMotion.IsReduced");
            return false;
        }
    };

    // ONE thin house for "ask kMotion.IsReduced under the caller's own label":
    // kDrawer.ReducedMotionActive and kTabIndicator.PrefersReducedMotion were
    // byte-identical apart from the label (findings 10368/10473). The label stays the
    // caller's verbatim text. kHeatmapGrid.IsReducedMotion is deliberately NOT routed
    // here - its KErrorMode default is TRUE (fail-closed), these two default FALSE.
    public static ReducedMotionThru = (Label: string): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            return kMotion.IsReduced();
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: the report name must stay the CALLER's verbatim
            // label (kDrawer.ReducedMotionActive / kTabIndicator.PrefersReducedMotion);
            // a "kMotion.ReducedMotionThru" literal here would misreport the owner.
            ks.KGlobalErrorHandler(e, Label);
            return false;
        }
    };

    /** True when the operating system asks for reduced motion, WHATEVER the product decided (RespectSystemSetting is not consulted).
     *  For a notice only - kDeck's presenter bar says "OS asks for reduced motion - C = calm" - never for a decision. */
    public static OsAsksReduced = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            return kMotion.OsProbeSafe("kMotion.OsAsksReduced");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMotion.OsAsksReduced");
            return false;
        }
    };

    /** The reduced-motion probe with its gate and error shell: Label is the caller's own name in a
        KGlobalErrorHandler report, so every caller keeps its exact error label while the shell lives once. */
    public static OsProbeSafe = (Label: string): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            return kMotion.OsProbe();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMotion.OsProbeSafe");
            return false;
        }
    };

    /** The raw OS ask: window.matchMedia('(prefers-reduced-motion: reduce)').matches - false without a
        window or without matchMedia. The one probe IsReduced, OsAsksReduced and kConfetti.PrefersReducedMotion shared. */
    private static OsProbe = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (typeof window === "undefined") { return false; }
            if (typeof window.matchMedia !== "function") { return false; }
            return window.matchMedia("(prefers-reduced-motion: reduce)").matches === true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMotion.OsProbe");
            return false;
        }
    };

    /** Publish the current decision to CSS. Idempotent; safe to call repeatedly. */
    public static Sync = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (typeof document === "undefined") { return; }
            const Root: HTMLElement | null = document.documentElement;
            if (Root === null) { return; }
            if (kMotion.IsReduced() === true) {
                Root.setAttribute("data-kreducedmotion", "1");
                return;
            }
            Root.removeAttribute("data-kreducedmotion");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMotion.Sync");
        }
    };
}

/**
 * kSpring - spring easing WITHOUT an animation library.
 *
 * A spring is the one thing a cubic-bezier genuinely cannot express: it
 * overshoots the target and settles back, and the amount it overshoots is a
 * consequence of physics rather than of four hand-tuned control points. That
 * is most of what an animation engine is actually selling.
 *
 * It does NOT need an engine. This class integrates a damped harmonic
 * oscillator once, up front, and emits the result as a CSS `linear()` easing
 * string - so the BROWSER runs the animation, on the compositor, exactly as it
 * runs every other transition in this library. No rAF loop, no ticker, no
 * dependency, nothing on the main thread per frame. (This is the same trick
 * motion.dev uses internally; evaluated 2026-07-31 and not adopted because
 * springs were the only thing it offered that we could not already do.)
 *
 * Because the output is a plain easing string it works with BOTH channels:
 *
 *     const S = kSpring.Css(kSpring.Bouncy);
 *     El.style.transition = "transform " + S.DurationMs + "ms " + S.Easing;
 *     // ...or the Web Animations API:
 *     El.animate([{ transform: "scale(0)" }, { transform: "scale(1)" }],
 *                { duration: S.DurationMs, easing: S.Easing });
 *
 * ALWAYS take the DurationMs from the same call. A spring curve is only correct
 * over its own settling time; run it for 200ms when it wanted 620 and the
 * element stops dead mid-bounce.
 *
 * `linear()` is Chrome/Edge 113+. `Supported()` reports it and `Css()` falls
 * back to the closest cubic-bezier, so a caller never has to branch.
 */
export interface kSpringConfig {
    /** Pull toward the target. Higher = faster and tighter. Default 170. */
    Stiffness?: number;
    /** Resistance. Lower = more overshoot. Default 26. */
    Damping?: number;
    /** Higher = more sluggish. Default 1. */
    Mass?: number;
    /** Starting velocity, in units of the full distance per second. Default 0. */
    Velocity?: number;
    /** Safety stop for the settle search, ms. Default 5000. */
    MaxDurationMs?: number;
}

/** How close to 1 counts as settled, and how still it must be to stop. */
const kSpringRestDistance: number = 0.001;
const kSpringRestVelocity: number = 0.001;
const kSpringStepMs: number = 4;
const kSpringMaxDurationMs: number = 5000;
/** One sample per ~10ms reads as smooth; the bounds keep the CSS string sane. */
const kSpringMsPerSample: number = 10;
const kSpringMinSamples: number = 8;
const kSpringMaxSamples: number = 120;

export class kSpring {

    /** Barely overshoots - for layout moves, where a bounce reads as a glitch. */
    public static Gentle: kSpringConfig = { Stiffness: 120, Damping: 24 };
    /** Critically damped: the fastest arrival with NO overshoot at all.
     *  169 rather than the more usual 170 is deliberate and load-bearing -
     *  critical damping is c = 2*sqrt(k*m), and sqrt(169) is exactly 13, so
     *  this lands on zeta = 1.0 EXACTLY. At 170 the ratio is 0.9971, i.e.
     *  technically under-damped: the overshoot is about 2 ten-thousandths and
     *  invisible, but `Overshoots()` would answer true and the preset would
     *  then contradict its own name. Chosen by construction, not by luck. */
    public static Smooth: kSpringConfig = { Stiffness: 169, Damping: 26 };
    /** A small, confident overshoot. The sane default for a value changing. */
    public static Snappy: kSpringConfig = { Stiffness: 260, Damping: 26 };
    /** Obvious bounce - for a success tick or a counter landing, not for layout. */
    public static Bouncy: kSpringConfig = { Stiffness: 300, Damping: 18 };

    /** True when the browser can run a `linear()` easing (Chrome/Edge 113+). */
    public static Supported = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (typeof CSS === "undefined") { return false; }
            if (typeof CSS.supports !== "function") { return false; }
            return CSS.supports("transition-timing-function", "linear(0, 1)");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.Supported");
            return false;
        }
    };

    /** The easing string and the duration it is only correct over. Use both. */
    public static Css = (Config: kSpringConfig): { Easing: string; DurationMs: number } => {
        if (ks.KErrorMode === true) { return { Easing: "ease", DurationMs: 300 }; }
        try {
            const Duration: number = kSpring.DurationMs(Config);
            if (kSpring.Supported() === false) {
                return { Easing: kSpring.Fallback(Config), DurationMs: Duration };
            }
            return { Easing: kSpring.Easing(Config), DurationMs: Duration };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.Css");
            return { Easing: "ease", DurationMs: 300 };
        }
    };

    /** The raw `linear(...)` string. Prefer Css(), which also handles the
     *  no-support case and hands back the matching duration. */
    public static Easing = (Config: kSpringConfig): string => {
        if (ks.KErrorMode === true) { return "ease"; }
        try {
            const Duration: number = kSpring.DurationMs(Config);
            const Count: number = kSpring.SampleCount(Duration);
            const Points: string[] = [];
            let Index: number = 0;
            while (Index <= Count) {
                const Time: number = (Index / Count) * (Duration / 1000);
                Points.push(kSpring.Round(kSpring.Position(Config, Time)));
                Index = Index + 1;
            }
            // Pin the ends: floating point must never leave an easing that
            // starts a hair off 0 or lands a hair off 1.
            Points[0] = "0";
            Points[Points.length - 1] = "1";
            return "linear(" + Points.join(", ") + ")";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.Easing");
            return "ease";
        }
    };

    /** Position at time `Seconds`, starting at 0 and settling on 1. May exceed
     *  1 - that overshoot is the whole point, and `linear()` permits it. */
    public static Position = (Config: kSpringConfig, Seconds: number): number => {
        if (ks.KErrorMode === true) { return 0; }
        try {
            if (Seconds <= 0) { return 0; }
            const Omega: number = kSpring.Omega(Config);
            const Zeta: number = kSpring.Zeta(Config);
            const Velocity: number = kSpring.Number(Config.Velocity, 0);
            const Decay: number = Math.exp(-Zeta * Omega * Seconds);
            // Solved on the ERROR term y = 1 - position, with y(0) = 1 and
            // y'(0) = -Velocity; the position is 1 - y.
            if (Zeta < 1) { return 1 - (Decay * kSpring.UnderDamped(Omega, Zeta, Velocity, Seconds)); }
            if (Zeta > 1) { return 1 - (Decay * kSpring.OverDamped(Omega, Zeta, Velocity, Seconds)); }
            return 1 - (Decay * (1 + (((Zeta * Omega) - Velocity) * Seconds)));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.Position");
            return 0;
        }
    };

    /** Oscillating regime - this is the one that overshoots. */
    private static UnderDamped = (Omega: number, Zeta: number, Velocity: number,
        Seconds: number): number => {
        if (ks.KErrorMode === true) { return 0; }
        try {
            const Damped: number = Omega * Math.sqrt(1 - (Zeta * Zeta));
            if (Damped === 0) { return 1; }
            const Scale: number = ((Zeta * Omega) - Velocity) / Damped;
            return Math.cos(Damped * Seconds) + (Scale * Math.sin(Damped * Seconds));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.UnderDamped");
            return 0;
        }
    };

    /** Sluggish regime - crawls to the target and never crosses it. */
    private static OverDamped = (Omega: number, Zeta: number, Velocity: number,
        Seconds: number): number => {
        if (ks.KErrorMode === true) { return 0; }
        try {
            const Rate: number = Omega * Math.sqrt((Zeta * Zeta) - 1);
            if (Rate === 0) { return 1; }
            const Scale: number = ((Zeta * Omega) - Velocity) / Rate;
            return Math.cosh(Rate * Seconds) + (Scale * Math.sinh(Rate * Seconds));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.OverDamped");
            return 0;
        }
    };

    /** How long the spring actually needs: stepped forward until it is both
     *  close enough to 1 AND slow enough to stay there. A spring has no
     *  duration of its own - it is a consequence of the constants. */
    public static DurationMs = (Config: kSpringConfig): number => {
        if (ks.KErrorMode === true) { return 300; }
        try {
            const Limit: number = kSpring.Number(Config.MaxDurationMs, kSpringMaxDurationMs);
            let Elapsed: number = kSpringStepMs;
            let Previous: number = 0;
            while (Elapsed < Limit) {
                const Value: number = kSpring.Position(Config, Elapsed / 1000);
                const Speed: number = Math.abs(Value - Previous);
                if (Math.abs(1 - Value) < kSpringRestDistance && Speed < kSpringRestVelocity) {
                    return Elapsed;
                }
                Previous = Value;
                Elapsed = Elapsed + kSpringStepMs;
            }
            return Limit;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.DurationMs");
            return 300;
        }
    };

    /** The closest cubic-bezier for browsers without `linear()`. Two curves,
     *  chosen on whether the spring overshoots - a bezier cannot exceed 1 on
     *  the Y axis without control points outside [0,1], and the overshooting
     *  form looks wrong applied to a spring that never bounces. */
    public static Fallback = (Config: kSpringConfig): string => {
        if (ks.KErrorMode === true) { return "ease"; }
        try {
            if (kSpring.Overshoots(Config) === true) {
                return "cubic-bezier(0.34, 1.56, 0.64, 1)";
            }
            return "cubic-bezier(0.22, 1, 0.36, 1)";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.Fallback");
            return "ease";
        }
    };

    /** True when the damping ratio is below 1, i.e. the spring crosses its
     *  target at least once. */
    public static Overshoots = (Config: kSpringConfig): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            return kSpring.Zeta(Config) < 1;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.Overshoots");
            return false;
        }
    };

    /** Undamped angular frequency, sqrt(k/m). */
    private static Omega = (Config: kSpringConfig): number => {
        if (ks.KErrorMode === true) { return 1; }
        try {
            const Stiffness: number = Math.max(1, kSpring.Number(Config.Stiffness, 170));
            const Mass: number = Math.max(0.01, kSpring.Number(Config.Mass, 1));
            return Math.sqrt(Stiffness / Mass);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.Omega");
            return 1;
        }
    };

    /** Damping ratio, c / (2*sqrt(k*m)). Below 1 bounces, 1 is critical,
     *  above 1 crawls. */
    private static Zeta = (Config: kSpringConfig): number => {
        if (ks.KErrorMode === true) { return 1; }
        try {
            const Stiffness: number = Math.max(1, kSpring.Number(Config.Stiffness, 170));
            const Mass: number = Math.max(0.01, kSpring.Number(Config.Mass, 1));
            const Damping: number = Math.max(0, kSpring.Number(Config.Damping, 26));
            return Damping / (2 * Math.sqrt(Stiffness * Mass));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.Zeta");
            return 1;
        }
    };

    private static SampleCount = (DurationMs: number): number => {
        if (ks.KErrorMode === true) { return kSpringMinSamples; }
        try {
            const Wanted: number = Math.round(DurationMs / kSpringMsPerSample);
            if (Wanted < kSpringMinSamples) { return kSpringMinSamples; }
            if (Wanted > kSpringMaxSamples) { return kSpringMaxSamples; }
            return Wanted;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.SampleCount");
            return kSpringMinSamples;
        }
    };

    /** Three decimals: enough for a curve the eye reads at 60fps, and it keeps
     *  a 120-point easing string under about 800 characters. */
    private static Round = (Value: number): string => {
        if (ks.KErrorMode === true) { return "0"; }
        try {
            return KNum3(Value, "kSpring.Round");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.Round");
            return "0";
        }
    };

    private static Number = (Value: number | undefined, Fallback: number): number => {
        if (ks.KErrorMode === true) { return Fallback; }
        try {
            if (Value === undefined) { return Fallback; }
            if (typeof Value !== "number") { return Fallback; }
            if (isFinite(Value) === false) { return Fallback; }
            return Value;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSpring.Number");
            return Fallback;
        }
    };
}
